import { apiSlice } from "../../../app/apiSlice";

const apiSliceWithTags = apiSlice.enhanceEndpoints({
  addTagTypes: ["Record", "Comment", "Trait"],
});

const recordApiSlice = apiSliceWithTags.injectEndpoints({
  endpoints: (builder) => ({
    getRecordsBySubjectAndClass: builder.query({
      query: ({ subject_id, classroom_id, include_raw = false }) =>
        `/record/subject?subject_id=${subject_id}&classroom_id=${classroom_id}${include_raw ? "&include_raw=true" : ""}`,
      providesTags: ["Record"],
    }),
    createRecord: builder.mutation({
      query: (values) => ({
        url: "/record/create",
        method: "POST",
        body: { ...values },
      }),
      invalidatesTags: ["Record"],
    }),
    deleteRecordsBySubjectAndClass: builder.mutation({
      query: (values) => ({
        url: `/record/${values.subjectId}/${values.classId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Record"],
    }),
    deleteRecordsBySubjectClassAndAssessment: builder.mutation({
      query: (values) => ({
        url: `/record/${values.subjectId}/${values.classId}/${values.assessmentId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Record"],
    }),
    deleteRecordsByStudents: builder.mutation({
      query: (values) => ({
        url: "/record/by-students",
        method: "DELETE",
        body: { ...values },
      }),
      invalidatesTags: ["Record", "Comment"],
    }),
    getCommentByStudent: builder.query({
      query: ({ student_id, type = "term" }) =>
        `/comment/student/${student_id}?type=${type}`,
      providesTags: ["Comment"],
    }),
    getCommentBySubjectAndClassroom: builder.query({
      query: ({ subject_id, classroom_id, type = "term" }) =>
        `/comment/subject/${subject_id}/${classroom_id}?type=${type}`,
      providesTags: ["Comment"],
    }),
    getCommentBySectionAndStudent: builder.query({
      query: ({ section_id, student_id, type = "term" }) =>
        `/comment/section/${section_id}/${student_id}?type=${type}`,
      providesTags: ["Comment"],
    }),
    getCommentByDepartmentAndStudent: builder.query({
      query: ({ department_id, student_id, type = "term" }) =>
        `/comment/department/${department_id}/${student_id}?type=${type}`,
      providesTags: ["Comment"],
    }),
    createComment: builder.mutation({
      query: (values) => ({
        url: "/comment/create",
        method: "POST",
        body: { ...values },
      }),
      invalidatesTags: ["Comment"],
    }),
    deleteComment: builder.mutation({
      query: (values) => ({
        url: `/comment/delete`,
        method: "DELETE",
        body: { ...values },
      }),
      invalidatesTags: ["Comment"],
    }),
    getStudentTraits: builder.query({
      query: ({ student_id, classroom_id }) =>
        `/trait/grade/student/${student_id}/${classroom_id}`,
      providesTags: ["Trait"],
    }),
    getTraitsForClassroom: builder.query({
      query: (classroom_id) => `/trait/for-classroom/${classroom_id}`,
      providesTags: ["Trait"],
    }),
    createStudentTraits: builder.mutation({
      query: (values) => ({
        url: "/trait/grade/create",
        method: "POST",
        body: { ...values },
      }),
      invalidatesTags: ["Trait"],
    }),
    deleteStudentTraits: builder.mutation({
      query: (values) => ({
        url: "/trait/grade/delete",
        method: "DELETE",
        body: { ...values },
      }),
      invalidatesTags: ["Trait"],
    }),
    getRecordLog: builder.query({
      query: ({
        page = 1,
        per_page = 10,
        recorded_by,
        classroom_id,
        subject_id,
        assessment_id,
        student_id,
      } = {}) => ({
        url: `/record/log`,
        params: {
          page,
          per_page,
          ...(recorded_by ? { recorded_by } : {}),
          ...(classroom_id ? { classroom_id } : {}),
          ...(subject_id ? { subject_id } : {}),
          ...(assessment_id ? { assessment_id } : {}),
          ...(student_id ? { student_id } : {}),
        },
      }),
      providesTags: ["Record"],
    }),
    generateAiComments: builder.mutation({
      query: (formData) => ({
        url: "/comment/ai/generate",
        method: "POST",
        body: formData,
        // FormData — do NOT set Content-Type; browser sets multipart boundary automatically
        formData: true,
      }),
    }),
    confirmAiComments: builder.mutation({
      query: (values) => ({
        url: "/comment/ai/confirm",
        method: "POST",
        body: { ...values },
      }),
      invalidatesTags: ["Comment"],
    }),
    extractAiScores: builder.mutation({
      query: (formData) => ({
        url: "/record/ai/extract",
        method: "POST",
        body: formData,
        formData: true,
      }),
    }),
    revertQuizPost: builder.mutation({
      query: ({ quiz_id, ...body }) => ({
        url: `/record/quiz-post/${quiz_id}`,
        method: "DELETE",
        body,
      }),
      invalidatesTags: ["Record"],
    }),
    checkQuizPosted: builder.query({
      query: (quiz_id) => `/record/quiz-post-check/${quiz_id}`,
      providesTags: ["Record"],
    }),
  }),
});

export const {
  useGetRecordsBySubjectAndClassQuery,
  useCreateRecordMutation,
  useCreateCommentMutation,
  useGetCommentByStudentQuery,
  useGetCommentBySubjectAndClassroomQuery,
  useGetCommentBySectionAndStudentQuery,
  useGetCommentByDepartmentAndStudentQuery,
  useDeleteRecordsBySubjectAndClassMutation,
  useDeleteRecordsBySubjectClassAndAssessmentMutation,
  useDeleteRecordsByStudentsMutation,
  useDeleteCommentMutation,
  useGetStudentTraitsQuery,
  useGetTraitsForClassroomQuery,
  useCreateStudentTraitsMutation,
  useDeleteStudentTraitsMutation,
  useGetRecordLogQuery,
  useGenerateAiCommentsMutation,
  useConfirmAiCommentsMutation,
  useExtractAiScoresMutation,
  useRevertQuizPostMutation,
  useCheckQuizPostedQuery,
} = recordApiSlice;
